import { TranslatePipe } from "@ngx-translate/core";
import { SvgIconComponent } from "angular-svg-icon";
import { FlatpickrDirective, provideFlatpickrDefaults } from "angularx-flatpickr";

import { ChangeDetectionStrategy, Component, input, linkedSignal, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";

export const PERIOD_OFFSETS = {
  "1D": 1000 * 3600 * 24,
  "1W": 1000 * 3600 * 24 * 7,
  "1M": 1000 * 3600 * 24 * 7 * 31,
  "1Y": 1000 * 3600 * 24 * 7 * 31 * 12,
};

export type PresetPeriod = "1D" | "1W" | "1M" | "1Y";

export const DEFAULT_START_DATE = newDateWithOffset(Date.now(), -PERIOD_OFFSETS["1M"]);
export const DEFAULT_END_DATE = new Date();

@Component({
  selector: "date-picker",
  providers: [provideFlatpickrDefaults()],
  imports: [FlatpickrDirective, FormsModule, SvgIconComponent, TranslatePipe],
  templateUrl: "./date-picker.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./date-picker.component.scss",
})
export class DatePicker {
  public readonly dateRangeChange = output<[Date, Date]>();
  public readonly defaultPeriod = input<PresetPeriod>("1M");

  protected readonly availablePeriods: PresetPeriod[] = ["1D", "1W", "1M", "1Y"];
  protected readonly activePeriod = linkedSignal<PresetPeriod | "">(() => this.defaultPeriod());
  protected readonly isDefault = signal(true);

  protected readonly firstDate = linkedSignal(() =>
    newDateWithOffset(new Date().getTime(), -PERIOD_OFFSETS[this.defaultPeriod()]),
  );
  protected readonly secondDate = linkedSignal(() => new Date());
  protected readonly tomorrow = new Date(new Date().setDate(new Date().getDate() + 1));

  protected setActivePeriod(period: PresetPeriod) {
    this.activePeriod.set(period);
    this.secondDate.set(new Date());
    this.firstDate.set(newDateWithOffset(this.secondDate().getTime(), -PERIOD_OFFSETS[period]));
    this.dateRangeChange.emit([this.firstDate(), this.secondDate()]);
  }

  protected reset() {
    const period = this.defaultPeriod();
    this.secondDate.set(new Date());
    this.firstDate.set(newDateWithOffset(this.secondDate().getTime(), -PERIOD_OFFSETS[period]));
    this.activePeriod.set(period);
    this.isDefault.set(true);
    this.dateRangeChange.emit([this.firstDate(), this.secondDate()]);
  }

  protected onFirstDateChange(date: Date | null) {
    if (!date) return;
    this.firstDate.set(date);
    this.onDateChange();
  }

  protected onSecondDateChange(date: Date | null) {
    if (!date) return;
    this.secondDate.set(date);
    this.onDateChange();
  }

  private onDateChange() {
    this.isDefault.set(false);
    this.activePeriod.set("");
    if (this.secondDate() > new Date()) {
      this.secondDate.set(new Date());
    }
    if (this.firstDate() > this.secondDate()) {
      this.firstDate.set(newDateWithOffset(this.secondDate().getTime(), -PERIOD_OFFSETS[this.defaultPeriod()]));
    }
    this.dateRangeChange.emit([this.firstDate(), this.secondDate()]);
  }
}

function newDateWithOffset(base: number, offset: number) {
  return new Date(base + offset);
}
